import type {
  LawQuestionnaireCategoryRule,
  LawQuestionnaireQuestion,
  LawQuestionnaireQuestionGroup,
  ProductLawApplicabilityStatus,
  ProductLawKey,
} from '@/shared/hooks/api/product'
import {
  getCraClearAnswer,
  getQuestionDraftAnswer,
  groupLawQuestionnaireQuestions,
  type LawQuestionnaireAnswerDraft,
  type LawQuestionnaireGroupedQuestions,
} from './lawApplicabilityQuestionnaire'

export type LawQuestionnaireSectionPill =
  | 'notStarted'
  | 'inProgress'
  | 'passed'
  | 'blocked'
  | 'determinesNotApplicable'
  | 'skipped'

export type LawQuestionnaireSectionCopy = {
  key: string
  params?: Record<string, string>
}

export type LawQuestionnaireSectionState = {
  pill: LawQuestionnaireSectionPill
  copy: LawQuestionnaireSectionCopy
}

export type LawQuestionnaireStatusBanner = {
  leadKey: string
  bodyKey: string
  bodyParams?: Record<string, string>
}

const ENG2317_LAWS: ProductLawKey[] = [
  'rohs',
  'weee',
  'emc',
  'lvd',
  'red',
  'machinery',
]

export function isEng2317QuestionnaireLaw(
  law: ProductLawKey
): law is (typeof ENG2317_LAWS)[number] {
  return ENG2317_LAWS.includes(law)
}

function countAnswers(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  expected: boolean | null
): number {
  return questions.filter((question) => {
    return getQuestionDraftAnswer(question, draft) === expected
  }).length
}

function getQuestionsForGroup(
  grouped: LawQuestionnaireGroupedQuestions[],
  group: LawQuestionnaireQuestionGroup
): LawQuestionnaireQuestion[] {
  return grouped.find((entry) => entry.group === group)?.questions ?? []
}

function getGateAnswer(
  inScopeQuestions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): boolean | null {
  const gate = inScopeQuestions[0]
  if (gate == null) {
    return null
  }
  return getQuestionDraftAnswer(gate, draft)
}

function questionHasGap(
  question: LawQuestionnaireQuestion,
  draft: LawQuestionnaireAnswerDraft
): boolean {
  if (Object.prototype.hasOwnProperty.call(draft, question.questionKey)) {
    return false
  }
  return question.hasGap
}

function isPackIncomplete(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): boolean {
  return questions.some((question) => {
    if (getQuestionDraftAnswer(question, draft) === null) {
      return true
    }
    return questionHasGap(question, draft)
  })
}

function labelsForYesQuestions(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): string[] {
  return questions
    .filter((question) => getQuestionDraftAnswer(question, draft) === true)
    .map((question) => question.label)
}

function firstYesQuestionLabel(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): string | null {
  const match = questions.find((question) => {
    return getQuestionDraftAnswer(question, draft) === true
  })
  return match?.label ?? null
}

function lawSectionKey(
  law: ProductLawKey,
  group: LawQuestionnaireQuestionGroup,
  condition: string
): string {
  if (law === 'cra' || isEng2317QuestionnaireLaw(law)) {
    return `v2.applicability.questionnaire.laws.${law}.sections.${group}.copy.${condition}`
  }
  return `v2.applicability.questionnaire.sections.generic.copy.${condition}`
}

function sectionState(
  law: ProductLawKey,
  group: LawQuestionnaireQuestionGroup,
  pill: LawQuestionnaireSectionPill,
  condition: string,
  params?: Record<string, string>
): LawQuestionnaireSectionState {
  return {
    pill,
    copy: {
      key: lawSectionKey(law, group, condition),
      params,
    },
  }
}

function categoriesPassed(
  categoryQuestions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule
): boolean {
  if (categoryQuestions.length === 0 || categoryRule == null) {
    return true
  }
  const yesCount = countAnswers(categoryQuestions, draft, true)
  const unansweredCount = countAnswers(categoryQuestions, draft, null)
  if (categoryRule === 'any') {
    return yesCount > 0
  }
  return yesCount === 1 && unansweredCount === 0
}

function resolveRohsWeeeGateSection(
  law: 'rohs' | 'weee',
  group: 'inScope',
  gateAnswer: boolean | null
): LawQuestionnaireSectionState {
  if (gateAnswer === null) {
    return sectionState(law, group, 'inProgress', 'gateUnanswered')
  }
  if (gateAnswer === true) {
    const lawName = law === 'rohs' ? 'RoHS' : 'WEEE'
    return sectionState(law, group, 'passed', 'gateYes', { law: lawName })
  }
  const lawName = law === 'rohs' ? 'RoHS' : 'WEEE'
  return sectionState(law, group, 'determinesNotApplicable', 'gateNo', {
    law: lawName,
  })
}

function resolveRohsCategoriesSection(
  gateAnswer: boolean | null,
  categoryQuestions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): LawQuestionnaireSectionState {
  const law = 'rohs' as const
  const group = 'categories' as const

  if (gateAnswer === null) {
    return sectionState(law, group, 'notStarted', 'gateUnanswered')
  }
  if (gateAnswer === false) {
    return sectionState(law, group, 'skipped', 'settledAbove')
  }

  const yesCount = countAnswers(categoryQuestions, draft, true)
  const noCount = countAnswers(categoryQuestions, draft, false)
  const total = categoryQuestions.length

  if (yesCount === 0 && noCount === 0) {
    return sectionState(law, group, 'inProgress', 'noneAnswered')
  }
  if (yesCount > 0) {
    return sectionState(law, group, 'passed', 'atLeastOneYes', {
      categories: labelsForYesQuestions(categoryQuestions, draft).join(', '),
    })
  }
  if (noCount === total) {
    return sectionState(law, group, 'blocked', 'allNo')
  }
  return sectionState(law, group, 'inProgress', 'someNoRestBlank')
}

function resolveWeeeCategoriesSection(
  gateAnswer: boolean | null,
  categoryQuestions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): LawQuestionnaireSectionState {
  const law = 'weee' as const
  const group = 'categories' as const

  if (gateAnswer === null) {
    return sectionState(law, group, 'notStarted', 'gateUnanswered')
  }
  if (gateAnswer === false) {
    return sectionState(law, group, 'skipped', 'settledAbove')
  }

  const yesQuestions = categoryQuestions.filter((question) => {
    return getQuestionDraftAnswer(question, draft) === true
  })
  const yesCount = yesQuestions.length
  const noCount = countAnswers(categoryQuestions, draft, false)
  const unansweredCount = countAnswers(categoryQuestions, draft, null)
  const total = categoryQuestions.length

  if (yesCount === 0 && noCount === 0 && unansweredCount === total) {
    return sectionState(law, group, 'inProgress', 'noneAnswered')
  }
  if (yesCount >= 2) {
    return sectionState(law, group, 'blocked', 'twoOrMoreYes', {
      categoryA: yesQuestions[0]?.label ?? '',
      categoryB: yesQuestions[1]?.label ?? '',
    })
  }
  if (yesCount === 1 && unansweredCount === 0 && noCount === total - 1) {
    return sectionState(law, group, 'passed', 'oneYesAllOthersNo', {
      category: yesQuestions[0]?.label ?? '',
    })
  }
  if (yesCount === 1 && unansweredCount > 0) {
    return sectionState(law, group, 'inProgress', 'oneYesOthersBlank', {
      category: yesQuestions[0]?.label ?? '',
    })
  }
  if (noCount === total) {
    return sectionState(law, group, 'blocked', 'allNo')
  }
  return sectionState(law, group, 'inProgress', 'noneAnswered')
}

function resolveRohsWeeeExclusionsSection(
  law: 'rohs' | 'weee',
  gateAnswer: boolean | null,
  categoryQuestions: LawQuestionnaireQuestion[],
  exclusionQuestions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule
): LawQuestionnaireSectionState {
  const group = 'exclusions' as const

  if (gateAnswer === false) {
    return sectionState(law, group, 'skipped', 'settledAbove')
  }
  if (
    gateAnswer === true &&
    !categoriesPassed(categoryQuestions, draft, categoryRule)
  ) {
    return sectionState(law, group, 'notStarted', 'categoriesUnresolved')
  }

  const yesLabel = firstYesQuestionLabel(exclusionQuestions, draft)
  if (yesLabel != null) {
    const lawName = law === 'rohs' ? 'RoHS' : 'WEEE'
    return sectionState(law, group, 'determinesNotApplicable', 'anyYes', {
      exclusion: yesLabel,
      law: lawName,
    })
  }

  const unansweredCount = countAnswers(exclusionQuestions, draft, null)
  const noCount = countAnswers(exclusionQuestions, draft, false)
  const total = exclusionQuestions.length

  if (noCount === total) {
    const lawName = law === 'rohs' ? 'RoHS' : 'WEEE'
    return sectionState(law, group, 'passed', 'allNo', { law: lawName })
  }
  if (unansweredCount > 0) {
    const lawName = law === 'rohs' ? 'RoHS' : 'WEEE'
    return sectionState(law, group, 'inProgress', 'noYesSomeBlank', {
      law: lawName,
    })
  }
  return sectionState(law, group, 'inProgress', 'noYesSomeBlank', {
    law: law === 'rohs' ? 'RoHS' : 'WEEE',
  })
}

function getPedScopeGateAnswer(
  inScopeQuestions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): boolean | null {
  const q1Question = inScopeQuestions.find((question) => {
    return question.questionKey === 'q1'
  })
  const q2Question = inScopeQuestions.find((question) => {
    return question.questionKey === 'q2'
  })
  const q1 =
    q1Question != null ? getQuestionDraftAnswer(q1Question, draft) : null
  const q2 =
    q2Question != null ? getQuestionDraftAnswer(q2Question, draft) : null

  if (q1 === false || q2 === false) {
    return false
  }
  if (q1 === true && q2 === true) {
    return true
  }
  return null
}

function resolvePedInScopeSection(
  inScopeQuestions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): LawQuestionnaireSectionState {
  const law = 'ped' as const
  const group = 'inScope' as const
  const q1Question = inScopeQuestions.find((question) => {
    return question.questionKey === 'q1'
  })
  const q2Question = inScopeQuestions.find((question) => {
    return question.questionKey === 'q2'
  })
  const q1 =
    q1Question != null ? getQuestionDraftAnswer(q1Question, draft) : null
  const q2 =
    q2Question != null ? getQuestionDraftAnswer(q2Question, draft) : null

  if (q1 === false || q2 === false) {
    return sectionState(law, group, 'determinesNotApplicable', 'gateNo')
  }
  if (q1 === true && q2 === true) {
    return sectionState(law, group, 'passed', 'gateYes')
  }
  return sectionState(law, group, 'inProgress', 'gateUnanswered')
}

function resolveInScopeWithExclusionsGateSection(
  law: 'emc' | 'lvd' | 'red' | 'machinery' | 'ped',
  gateAnswer: boolean | null
): LawQuestionnaireSectionState {
  const group = 'inScope' as const
  if (gateAnswer === null) {
    return sectionState(law, group, 'inProgress', 'gateUnanswered')
  }
  if (gateAnswer === true) {
    return sectionState(law, group, 'passed', 'gateYes')
  }
  return sectionState(law, group, 'determinesNotApplicable', 'gateNo')
}

function resolveInScopeWithExclusionsExclusionSection(
  law: 'emc' | 'lvd' | 'red' | 'machinery' | 'ped',
  gateAnswer: boolean | null,
  exclusionQuestions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  requiresCompletePackBeforeVerdict: boolean,
  allQuestions: LawQuestionnaireQuestion[]
): LawQuestionnaireSectionState {
  const group = 'exclusions' as const

  if (gateAnswer === null) {
    return sectionState(law, group, 'notStarted', 'gateUnanswered')
  }
  if (gateAnswer === false) {
    if (
      requiresCompletePackBeforeVerdict &&
      isPackIncomplete(allQuestions, draft)
    ) {
      return sectionState(law, group, 'inProgress', 'completePackForResult')
    }
    return sectionState(law, group, 'skipped', 'settledAbove')
  }

  const yesLabel = firstYesQuestionLabel(exclusionQuestions, draft)
  if (yesLabel != null) {
    return sectionState(law, group, 'determinesNotApplicable', 'anyYes', {
      exclusion: yesLabel,
    })
  }

  const unansweredCount = countAnswers(exclusionQuestions, draft, null)
  const noCount = countAnswers(exclusionQuestions, draft, false)
  const total = exclusionQuestions.length

  if (noCount === total) {
    return sectionState(law, group, 'passed', 'allNo')
  }
  if (unansweredCount > 0) {
    return sectionState(law, group, 'inProgress', 'noYesSomeBlank')
  }
  return sectionState(law, group, 'inProgress', 'noYesSomeBlank')
}

function resolveMachineryGateSection(
  gateAnswer: boolean | null,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  requiresCompletePackBeforeVerdict: boolean
): LawQuestionnaireSectionState {
  const law = 'machinery' as const
  const group = 'inScope' as const

  if (gateAnswer === null) {
    return sectionState(law, group, 'inProgress', 'gateUnanswered')
  }
  if (gateAnswer === true) {
    return sectionState(law, group, 'passed', 'gateYes')
  }
  if (requiresCompletePackBeforeVerdict && isPackIncomplete(questions, draft)) {
    return sectionState(law, group, 'inProgress', 'completePackForResult')
  }
  return sectionState(law, group, 'determinesNotApplicable', 'gateNo')
}

function craGroupDecides(
  group: LawQuestionnaireQuestionGroup,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): boolean {
  const answers = questions.map((question) => getCraClearAnswer(question, draft))
  if (group === 'inScope') {
    return answers.some((answer) => answer === false)
  }
  return answers.some((answer) => answer === true)
}

function craGroupPassed(
  group: LawQuestionnaireQuestionGroup,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): boolean {
  const answers = questions.map((question) => getCraClearAnswer(question, draft))
  if (answers.length === 0) {
    return false
  }
  if (group === 'inScope') {
    return answers.every((answer) => answer === true)
  }
  return answers.every((answer) => answer === false)
}

function resolveCraSection(
  group: LawQuestionnaireQuestionGroup,
  groupQuestions: LawQuestionnaireQuestion[],
  allGrouped: LawQuestionnaireGroupedQuestions[],
  draft: LawQuestionnaireAnswerDraft
): LawQuestionnaireSectionState {
  const law = 'cra' as const
  if (craGroupDecides(group, groupQuestions, draft)) {
    return sectionState(
      law,
      group,
      'determinesNotApplicable',
      'determinesNotApplicable'
    )
  }
  if (craGroupPassed(group, groupQuestions, draft)) {
    return sectionState(law, group, 'passed', 'passed')
  }
  const decidedElsewhere = allGrouped.some((entry) => {
    if (entry.group === group) {
      return false
    }
    return craGroupDecides(entry.group, entry.questions, draft)
  })
  if (decidedElsewhere) {
    return sectionState(law, group, 'skipped', 'skipped')
  }
  return sectionState(law, group, 'inProgress', 'inProgress')
}

function resolveGenericSection(
  law: ProductLawKey,
  group: LawQuestionnaireQuestionGroup,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule
): LawQuestionnaireSectionState {
  if (group === 'inScope') {
    const gateAnswer = getGateAnswer(questions, draft)
    if (gateAnswer === null) {
      return sectionState(law, group, 'inProgress', 'gateUnanswered')
    }
    if (gateAnswer === false) {
      return sectionState(law, group, 'determinesNotApplicable', 'gateNo')
    }
    return sectionState(law, group, 'passed', 'gateYes')
  }
  if (group === 'categories') {
    const yesCount = countAnswers(questions, draft, true)
    if (categoryRule === 'exactlyOne' && yesCount > 1) {
      return sectionState(law, group, 'blocked', 'twoOrMoreYes')
    }
    if (yesCount > 0) {
      return sectionState(law, group, 'passed', 'atLeastOneYes')
    }
    return sectionState(law, group, 'inProgress', 'noneAnswered')
  }
  const yesLabel = firstYesQuestionLabel(questions, draft)
  if (yesLabel != null) {
    return sectionState(law, group, 'determinesNotApplicable', 'anyYes', {
      exclusion: yesLabel,
    })
  }
  if (countAnswers(questions, draft, null) > 0) {
    return sectionState(law, group, 'inProgress', 'noYesSomeBlank')
  }
  return sectionState(law, group, 'passed', 'allNo')
}

export function getLawQuestionnaireSectionState(
  law: ProductLawKey,
  group: LawQuestionnaireQuestionGroup,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule,
  requiresCompletePackBeforeVerdict = false
): LawQuestionnaireSectionState {
  const grouped = groupLawQuestionnaireQuestions(questions)
  const inScopeQuestions = getQuestionsForGroup(grouped, 'inScope')
  const categoryQuestions = getQuestionsForGroup(grouped, 'categories')
  const exclusionQuestions = getQuestionsForGroup(grouped, 'exclusions')
  const gateAnswer = getGateAnswer(inScopeQuestions, draft)
  const groupQuestions = getQuestionsForGroup(grouped, group)

  if (law === 'cra') {
    return resolveCraSection(group, groupQuestions, grouped, draft)
  }

  if (law === 'rohs') {
    if (group === 'inScope') {
      return resolveRohsWeeeGateSection('rohs', group, gateAnswer)
    }
    if (group === 'categories') {
      return resolveRohsCategoriesSection(gateAnswer, categoryQuestions, draft)
    }
    return resolveRohsWeeeExclusionsSection(
      'rohs',
      gateAnswer,
      categoryQuestions,
      exclusionQuestions,
      draft,
      categoryRule
    )
  }

  if (law === 'weee') {
    if (group === 'inScope') {
      return resolveRohsWeeeGateSection('weee', group, gateAnswer)
    }
    if (group === 'categories') {
      return resolveWeeeCategoriesSection(gateAnswer, categoryQuestions, draft)
    }
    return resolveRohsWeeeExclusionsSection(
      'weee',
      gateAnswer,
      categoryQuestions,
      exclusionQuestions,
      draft,
      categoryRule
    )
  }

  if (law === 'ped') {
    const pedGateAnswer = getPedScopeGateAnswer(inScopeQuestions, draft)
    if (group === 'inScope') {
      return resolvePedInScopeSection(inScopeQuestions, draft)
    }
    return resolveInScopeWithExclusionsExclusionSection(
      'ped',
      pedGateAnswer,
      exclusionQuestions,
      draft,
      false,
      questions
    )
  }

  if (law === 'emc' || law === 'lvd' || law === 'red') {
    if (group === 'inScope') {
      return resolveInScopeWithExclusionsGateSection(law, gateAnswer)
    }
    return resolveInScopeWithExclusionsExclusionSection(
      law,
      gateAnswer,
      exclusionQuestions,
      draft,
      false,
      questions
    )
  }

  if (law === 'machinery') {
    if (group === 'inScope') {
      return resolveMachineryGateSection(
        gateAnswer,
        questions,
        draft,
        requiresCompletePackBeforeVerdict
      )
    }
    return resolveInScopeWithExclusionsExclusionSection(
      'machinery',
      gateAnswer,
      exclusionQuestions,
      draft,
      requiresCompletePackBeforeVerdict,
      questions
    )
  }

  return resolveGenericSection(law, group, groupQuestions, draft, categoryRule)
}

export function getLawQuestionnaireSectionPills(
  law: ProductLawKey,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule,
  requiresCompletePackBeforeVerdict = false
): Map<LawQuestionnaireQuestionGroup, LawQuestionnaireSectionPill> {
  const grouped = groupLawQuestionnaireQuestions(questions)
  const pills = new Map<LawQuestionnaireQuestionGroup, LawQuestionnaireSectionPill>()
  for (const entry of grouped) {
    pills.set(
      entry.group,
      getLawQuestionnaireSectionState(
        law,
        entry.group,
        questions,
        draft,
        categoryRule,
        requiresCompletePackBeforeVerdict
      ).pill
    )
  }
  return pills
}

export function getActiveLawQuestionnaireSection(
  grouped: LawQuestionnaireGroupedQuestions[],
  pills: Map<LawQuestionnaireQuestionGroup, LawQuestionnaireSectionPill>
): LawQuestionnaireQuestionGroup | null {
  for (const entry of grouped) {
    const pill = pills.get(entry.group)
    if (pill === 'blocked') {
      return entry.group
    }
  }
  for (const entry of grouped) {
    const pill = pills.get(entry.group)
    if (pill === 'inProgress') {
      return entry.group
    }
  }
  return null
}

export function getPreferredLawQuestionnaireGroupFromSections(
  law: ProductLawKey,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule,
  requiresCompletePackBeforeVerdict = false
): LawQuestionnaireQuestionGroup | null {
  const grouped = groupLawQuestionnaireQuestions(questions)
  if (grouped.length === 0) {
    return null
  }
  const pills = getLawQuestionnaireSectionPills(
    law,
    questions,
    draft,
    categoryRule,
    requiresCompletePackBeforeVerdict
  )
  const active = getActiveLawQuestionnaireSection(grouped, pills)
  if (active != null) {
    return active
  }
  return grouped[0]?.group ?? null
}

export function sectionPillBadgeVariant(
  pill: LawQuestionnaireSectionPill
): 'success' | 'danger' | 'warning' | 'outline' | 'secondary' {
  if (pill === 'passed') {
    return 'success'
  }
  if (pill === 'determinesNotApplicable') {
    return 'danger'
  }
  if (pill === 'blocked' || pill === 'inProgress') {
    return 'warning'
  }
  if (pill === 'notStarted' || pill === 'skipped') {
    return 'secondary'
  }
  return 'secondary'
}

export function getLawQuestionnaireSectionTitleKey(
  law: ProductLawKey,
  group: LawQuestionnaireQuestionGroup
): string {
  if (law === 'cra' || isEng2317QuestionnaireLaw(law)) {
    return `v2.applicability.questionnaire.laws.${law}.sections.${group}.title`
  }
  return `v2.applicability.questionnaire.groups.${group}.title`
}

export function getLawQuestionnaireSectionIntroKey(
  law: ProductLawKey,
  group: LawQuestionnaireQuestionGroup
): string {
  if (law === 'cra' || isEng2317QuestionnaireLaw(law)) {
    return `v2.applicability.questionnaire.laws.${law}.sections.${group}.intro`
  }
  return `v2.applicability.questionnaire.groups.${group}.explanation`
}

function isQuestionnaireUndecided(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): boolean {
  return questions.every((question) => {
    return getQuestionDraftAnswer(question, draft) === null
  })
}

function buildApplicableBannerBody(
  law: ProductLawKey,
  grouped: LawQuestionnaireGroupedQuestions[],
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule,
  requiresCompletePackBeforeVerdict: boolean
): LawQuestionnaireStatusBanner {
  const reasons: string[] = []
  for (const entry of grouped) {
    const state = getLawQuestionnaireSectionState(
      law,
      entry.group,
      questions,
      draft,
      categoryRule,
      requiresCompletePackBeforeVerdict
    )
    if (state.pill === 'passed') {
      reasons.push(state.copy.key)
    }
  }
  return {
    leadKey: 'v2.applicability.questionnaire.banner.applicable.lead',
    bodyKey: 'v2.applicability.questionnaire.banner.applicable.body',
    bodyParams: {
      reasonCount: String(reasons.length),
    },
  }
}

function buildNotApplicableBannerBody(
  law: ProductLawKey,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule,
  requiresCompletePackBeforeVerdict: boolean
): LawQuestionnaireStatusBanner {
  const grouped = groupLawQuestionnaireQuestions(questions)
  for (const entry of grouped) {
    const state = getLawQuestionnaireSectionState(
      law,
      entry.group,
      questions,
      draft,
      categoryRule,
      requiresCompletePackBeforeVerdict
    )
    if (state.pill === 'determinesNotApplicable') {
      return {
        leadKey: 'v2.applicability.questionnaire.banner.notApplicable.lead',
        bodyKey: state.copy.key,
        bodyParams: state.copy.params,
      }
    }
  }
  return {
    leadKey: 'v2.applicability.questionnaire.banner.notApplicable.lead',
    bodyKey: 'v2.applicability.questionnaire.banner.notApplicable.bodyFallback',
  }
}

export function getLawQuestionnaireStatusBanner(
  law: ProductLawKey,
  liveStatus: ProductLawApplicabilityStatus,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule,
  requiresCompletePackBeforeVerdict = false
): LawQuestionnaireStatusBanner {
  const grouped = groupLawQuestionnaireQuestions(questions)

  if (isQuestionnaireUndecided(questions, draft)) {
    return {
      leadKey: 'v2.applicability.questionnaire.banner.undecided.lead',
      bodyKey: 'v2.applicability.questionnaire.banner.undecided.body',
    }
  }

  if (liveStatus === 'APPLICABLE') {
    return buildApplicableBannerBody(
      law,
      grouped,
      questions,
      draft,
      categoryRule,
      requiresCompletePackBeforeVerdict
    )
  }

  if (liveStatus === 'NOT_APPLICABLE') {
    return buildNotApplicableBannerBody(
      law,
      questions,
      draft,
      categoryRule,
      requiresCompletePackBeforeVerdict
    )
  }

  const pills = getLawQuestionnaireSectionPills(
    law,
    questions,
    draft,
    categoryRule,
    requiresCompletePackBeforeVerdict
  )
  const activeGroup = getActiveLawQuestionnaireSection(grouped, pills)
  if (activeGroup != null) {
    const activeState = getLawQuestionnaireSectionState(
      law,
      activeGroup,
      questions,
      draft,
      categoryRule,
      requiresCompletePackBeforeVerdict
    )
    return {
      leadKey: 'v2.applicability.questionnaire.banner.stillDeciding.lead',
      bodyKey: activeState.copy.key,
      bodyParams: activeState.copy.params,
    }
  }

  return {
    leadKey: 'v2.applicability.questionnaire.banner.stillDeciding.lead',
    bodyKey: 'v2.applicability.questionnaire.banner.stillDeciding.bodyFallback',
  }
}
